import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  Plus, Edit, Trash2, X, Search, GripVertical, 
  Award, Users, Globe, Cpu, Shield, Star, Zap, Heart,
  Target, Lightbulb, CheckCircle, Clock
} from 'lucide-react';
import { featuresService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

// Map des icônes disponibles
const iconComponents = {
  award: Award,
  users: Users,
  globe: Globe,
  cpu: Cpu,
  shield: Shield,
  star: Star,
  zap: Zap,
  heart: Heart,
  target: Target,
  lightbulb: Lightbulb,
  check: CheckCircle,
  clock: Clock,
};

const iconOptions = [
  { value: 'award', label: 'Trophée', icon: Award },
  { value: 'users', label: 'Utilisateurs', icon: Users },
  { value: 'globe', label: 'Globe', icon: Globe },
  { value: 'cpu', label: 'Support', icon: Cpu },
  { value: 'shield', label: 'Bouclier', icon: Shield },
  { value: 'star', label: 'Étoile', icon: Star },
  { value: 'zap', label: 'Éclair', icon: Zap },
  { value: 'heart', label: 'Cœur', icon: Heart },
  { value: 'target', label: 'Cible', icon: Target },
  { value: 'lightbulb', label: 'Ampoule', icon: Lightbulb },
  { value: 'check', label: 'Check', icon: CheckCircle },
  { value: 'clock', label: 'Horloge', icon: Clock },
];

function FeaturesPage() {
  const [features, setFeatures] = useState([]);
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'award',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    loadFeatures();
  }, []);

  useEffect(() => {
    filterFeatures();
  }, [features, searchQuery, filterStatus]);

  const filterFeatures = () => {
    let filtered = [...features];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(feature =>
        feature.title.toLowerCase().includes(query) ||
        (feature.description || '').toLowerCase().includes(query)
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(feature =>
        filterStatus === 'active' ? feature.is_active : !feature.is_active
      );
    }

    setFilteredFeatures(filtered);
  };

  const loadFeatures = async () => {
    try {
      const response = await featuresService.getAllAdmin();
      console.log('Features chargés:', response.data);
      setFeatures(response.data.features || []);
    } catch (error) {
      console.error('Erreur:', error);
      // Fallback
      try {
        const response = await featuresService.getAll();
        setFeatures(response.data.features || []);
      } catch (err) {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const openModal = (feature = null) => {
    if (feature) {
      setEditing(feature);
      setFormData({
        title: feature.title || '',
        description: feature.description || '',
        icon: feature.icon || 'award',
        display_order: feature.display_order || 0,
        is_active: feature.is_active ? true : false
      });
    } else {
      setEditing(null);
      setFormData({
        title: '',
        description: '',
        icon: 'award',
        display_order: features.length + 1,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error('Le titre est requis');
      return;
    }

    try {
      if (editing) {
        await featuresService.update(editing.id, formData);
        toast.success('Avantage mis à jour');
      } else {
        await featuresService.create(formData);
        toast.success('Avantage créé');
      }
      setShowModal(false);
      loadFeatures();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cet avantage ?')) return;

    try {
      await featuresService.delete(id);
      toast.success('Avantage supprimé');
      loadFeatures();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (feature) => {
    try {
      await featuresService.update(feature.id, { is_active: !feature.is_active });
      toast.success('Statut mis à jour');
      loadFeatures();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
  };

  const getIconComponent = (iconName) => {
    return iconComponents[iconName] || Award;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Avantages</h1>
          <p className="text-dark-500">Gérez les points forts affichés sur la page d'accueil</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Nouvel avantage
        </button>
      </div>

      {/* Aperçu en temps réel */}
      <div className="card p-6 mb-6 bg-gradient-to-br from-dark-50 to-primary-50">
        <h3 className="text-dark-700 text-sm font-medium mb-4">Aperçu sur le site</h3>
        <div className="space-y-4">
          {features.filter(f => f.is_active).slice(0, 4).map((feature) => {
            const IconComponent = getIconComponent(feature.icon);
            return (
              <div key={feature.id} className="flex gap-4 items-start">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-soft flex-shrink-0">
                  <IconComponent className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-dark-900">{feature.title}</h3>
                  <p className="text-dark-600 text-sm">{feature.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="input pl-10"
              />
            </div>
          </div>

          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-100">
          <div className="flex items-center gap-4">
            {(searchQuery || filterStatus !== 'all') && (
              <button
                onClick={resetFilters}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
          <div className="text-sm text-dark-500">
            {filteredFeatures.length} avantage{filteredFeatures.length > 1 ? 's' : ''} affiché{filteredFeatures.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Liste */}
      {filteredFeatures.length === 0 ? (
        <div className="card p-12 text-center">
          <Award className="w-16 h-16 text-dark-300 mx-auto mb-4" />
          <p className="text-dark-500 mb-4">
            {features.length === 0
              ? 'Aucun avantage'
              : 'Aucun résultat pour ces critères'}
          </p>
          {features.length === 0 ? (
            <button onClick={() => openModal()} className="btn-primary">
              Ajouter un avantage
            </button>
          ) : (
            <button onClick={resetFilters} className="btn-ghost">
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {filteredFeatures.map((feature, index) => {
            const IconComponent = getIconComponent(feature.icon);
            return (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card p-6 relative group"
              >
                {/* Actions */}
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openModal(feature)}
                    className="p-2 bg-white rounded-lg shadow hover:bg-dark-50"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4 text-dark-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(feature.id)}
                    className="p-2 bg-white rounded-lg shadow hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>

                {/* Ordre */}
                <div className="absolute top-4 left-4">
                  <span className="flex items-center gap-1 text-xs text-dark-400 bg-dark-100 px-2 py-1 rounded">
                    <GripVertical className="w-3 h-3" />
                    #{feature.display_order}
                  </span>
                </div>

                {/* Contenu */}
                <div className="flex gap-4 mt-6">
                  <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-7 h-7 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-dark-900 text-lg mb-1">{feature.title}</h3>
                    <p className="text-dark-500 text-sm">{feature.description}</p>
                  </div>
                </div>

                {/* Status */}
                <div className="mt-4 pt-4 border-t border-dark-100 flex items-center justify-between">
                  <span className="text-xs text-dark-400">
                    Icône: {feature.icon}
                  </span>
                  <button
                    onClick={() => toggleActive(feature)}
                    className={`badge cursor-pointer ${feature.is_active ? 'badge-secondary' : 'bg-dark-100 text-dark-500'}`}
                  >
                    {feature.is_active ? 'Actif' : 'Inactif'}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-dark-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold">
                {editing ? 'Modifier l\'avantage' : 'Nouvel avantage'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Titre */}
              <div>
                <label className="label">Titre *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="input"
                  required
                  placeholder="Ex: 15+ ans d'expérience"
                />
              </div>

              {/* Description */}
              <div>
                <label className="label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input"
                  rows={3}
                  placeholder="Ex: Une expertise éprouvée dans la transformation digitale bancaire"
                />
              </div>

              {/* Icône */}
              <div>
                <label className="label">Icône</label>
                <div className="grid grid-cols-4 gap-2">
                  {iconOptions.map((option) => {
                    const IconComp = option.icon;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, icon: option.value }))}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                          formData.icon === option.value
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-dark-200 hover:border-dark-300'
                        }`}
                      >
                        <IconComp className={`w-6 h-6 ${formData.icon === option.value ? 'text-primary-600' : 'text-dark-500'}`} />
                        <span className="text-xs text-dark-500">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Aperçu */}
              <div className="p-4 bg-dark-50 rounded-xl">
                <p className="text-xs text-dark-400 mb-3">Aperçu</p>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-soft flex-shrink-0">
                    {(() => {
                      const IconComp = getIconComponent(formData.icon);
                      return <IconComp className="w-6 h-6 text-primary-600" />;
                    })()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-dark-900">{formData.title || 'Titre'}</h3>
                    <p className="text-dark-600 text-sm">{formData.description || 'Description'}</p>
                  </div>
                </div>
              </div>

              {/* Ordre d'affichage */}
              <div>
                <label className="label">Ordre d'affichage</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  className="input"
                  min="0"
                />
              </div>

              {/* Actif */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-dark-700">
                  Avantage actif (visible sur le site)
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default FeaturesPage;